import React, { useEffect } from 'react';

import { Typography, Divider, Box, Grid, Checkbox, FormControlLabel } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { Controller, useFormContext } from 'react-hook-form';

import {
  CPDEnrollmentStatusEnum,
  CPDLicenseTypeEnum,
  ICPDLicense,
  ICPDCourse,
  CPDRegulatorTypeEnum,
  LicenseRenewalTypeEnum,
  ApplicantTypeEnum,
} from '../../../../interfaces';

import { FormErrorText } from './style';

import LicenseForm from '@components/molecules/cpdEnrollment/licenseInformation/licenseForm';
import { CPDEnrollmentFieldNames } from 'src/helpers/constants';
import { dateFormatThai, getCpdLicenseTitleKey, getLicenseFieldNames } from 'src/helpers/utils';

type Props = {
  customerLicenses: ICPDLicense[];
  // cpdCourse: ICPDCourse;
  applicantType: ApplicantTypeEnum;
};

const OICLicenseFourthPlusRenewalForm = ({ customerLicenses, applicantType }: Props) => {
  const { control, watch, setValue, getValues } = useFormContext();
  const { t } = useTranslation();

  const customerLifeLicense = customerLicenses.find((license) => license.licenseType === CPDLicenseTypeEnum.LIFE);
  const customerNonLifeLicense = customerLicenses.find(
    (license) => license.licenseType === CPDLicenseTypeEnum.NON_LIFE,
  );
  const hasBothLicenses = !!customerLifeLicense && !!customerNonLifeLicense;
  const checkboxLife = watch(CPDEnrollmentFieldNames.isSelectOICLifeLicense);
  const checkboxNonLife = watch(CPDEnrollmentFieldNames.isSelectOICNonLifeLicense);
  const isRegulatorUnchecked = getValues(CPDEnrollmentFieldNames.status) === CPDEnrollmentStatusEnum.RegulatorUnChecked;

  useEffect(() => {
    if (customerLifeLicense) {
      const isSelectOICLifeLicense =
        getValues(CPDEnrollmentFieldNames.isSelectOICLifeLicense) === undefined
          ? true
          : getValues(CPDEnrollmentFieldNames.isSelectOICLifeLicense);

      setValue(
        CPDEnrollmentFieldNames.isSelectOICLifeLicense,
        getValues(CPDEnrollmentFieldNames.oicLifeLicenseNo) ? isSelectOICLifeLicense : true,
      );
    }
    if (customerNonLifeLicense) {
      const isSelectOICNonLifeLicense =
        getValues(CPDEnrollmentFieldNames.isSelectOICNonLifeLicense) === undefined
          ? true
          : getValues(CPDEnrollmentFieldNames.isSelectOICNonLifeLicense);

      setValue(
        CPDEnrollmentFieldNames.isSelectOICNonLifeLicense,
        getValues(CPDEnrollmentFieldNames.oicNonLifeLicenseNo) ? isSelectOICNonLifeLicense : true,
      );
    }
  }, []);

  if (!hasBothLicenses && !isRegulatorUnchecked) {
    let licenseName = null;
    let customerLicense = null;
    if (customerLifeLicense) {
      licenseName = 'Life';
      customerLicense = { ...customerLifeLicense };
    } else if (customerNonLifeLicense) {
      licenseName = 'NonLife';
      customerLicense = { ...customerNonLifeLicense };
    }

    const { licenseType } = customerLifeLicense || customerNonLifeLicense || {};
    const { licenseNoFieldName, licenseStartDateFieldName, licenseEndDateFieldName } = getLicenseFieldNames(
      licenseName!,
    );
    return (
      <>
        <Typography sx={{ typography: { md: 'h3', xs: 'h6' }, mt: { xs: '30px', lg: '50px' } }}>
          {t(
            getCpdLicenseTitleKey(
              CPDRegulatorTypeEnum.OIC,
              LicenseRenewalTypeEnum.NotSet,
              applicantType,
              licenseType as CPDLicenseTypeEnum,
            ),
          )}
        </Typography>
        <Divider sx={{ my: { xs: '8px', md: '12px' } }} />
        <LicenseForm
          licenseFieldName={licenseNoFieldName}
          startDateFieldName={licenseStartDateFieldName}
          endDateFieldName={licenseEndDateFieldName}
          license={customerLicense?.licenseNo}
          startDate={customerLicense?.createdDate ? dateFormatThai(customerLicense?.createdDate!) : undefined}
          endDate={customerLicense?.expiredDate ? dateFormatThai(customerLicense?.expiredDate!) : undefined}
          disabled={!isRegulatorUnchecked}
        />
      </>
    );
  }

  return (
    <Box sx={{ mt: { xs: '30px', lg: '50px' } }}>
      <Typography component="span" sx={{ color: 'errorDark.main' }}>
        *
      </Typography>
      &nbsp;
      <Typography component="span" sx={{ typography: { md: 'h3', xs: 'h6' } }}>
        {t('cpd_enrollment.license_information.oic.select_license')}
      </Typography>
      <Divider sx={{ my: { xs: '8px', md: '12px' } }} />
      <Grid container spacing={1}>
        <Grid xs={6} md={2} item>
          <Controller
            rules={{ required: !checkboxNonLife && true }}
            name={CPDEnrollmentFieldNames.isSelectOICLifeLicense}
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    defaultChecked={!!customerLifeLicense}
                    sx={{ color: !checkboxLife && !checkboxNonLife ? 'errorDark.main' : undefined }}
                    checked={field.value}
                    size="small"
                    {...field}
                  />
                }
                label={t('cpd_course.license_types.oic.life') as string}
              />
            )}
          />
        </Grid>
        <Grid xs={6} md={10} item>
          <Controller
            rules={{ required: !checkboxLife && true }}
            name={CPDEnrollmentFieldNames.isSelectOICNonLifeLicense}
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    defaultChecked={!!customerNonLifeLicense}
                    sx={{ color: !checkboxLife && !checkboxNonLife ? 'errorDark.main' : undefined }}
                    checked={field.value}
                    size="small"
                    {...field}
                  />
                }
                label={t('cpd_course.license_types.oic.non_life') as string}
              />
            )}
          />
        </Grid>
        <Grid xs={12} item>
          {!checkboxLife && !checkboxNonLife && (
            <FormErrorText sx={{ ml: 0, mt: '-10px' }}>
              {t('cpd_enrollment.license_information.error.checkbox')}
            </FormErrorText>
          )}
        </Grid>
      </Grid>
      {checkboxLife && (
        <Box>
          <Typography sx={{ typography: { md: 'h3', xs: 'h6' }, mt: { xs: '30px', lg: '50px' } }}>
            {t(
              getCpdLicenseTitleKey(
                CPDRegulatorTypeEnum.OIC,
                LicenseRenewalTypeEnum.NotSet,
                applicantType,
                CPDLicenseTypeEnum.LIFE,
              ),
            )}
          </Typography>
          <Divider sx={{ my: { xs: '8px', md: '12px' } }} />
          <LicenseForm
            licenseFieldName={CPDEnrollmentFieldNames.oicLifeLicenseNo}
            startDateFieldName={CPDEnrollmentFieldNames.oicLifeStartDate}
            endDateFieldName={CPDEnrollmentFieldNames.oicLifeEndDate}
            license={customerLifeLicense?.licenseNo}
            startDate={customerLifeLicense?.createdDate ? dateFormatThai(customerLifeLicense?.createdDate!) : undefined}
            endDate={customerLifeLicense?.expiredDate ? dateFormatThai(customerLifeLicense?.expiredDate!) : undefined}
            disabled={!isRegulatorUnchecked}
          />
        </Box>
      )}
      {checkboxNonLife && (
        <Box>
          <Typography sx={{ typography: { md: 'h3', xs: 'h6' }, mt: { xs: '30px', lg: '50px' } }}>
            {t(
              getCpdLicenseTitleKey(
                CPDRegulatorTypeEnum.OIC,
                LicenseRenewalTypeEnum.NotSet,
                applicantType,
                CPDLicenseTypeEnum.NON_LIFE,
              ),
            )}
          </Typography>
          <Divider sx={{ my: { xs: '8px', md: '12px' } }} />
          <LicenseForm
            licenseFieldName={CPDEnrollmentFieldNames.oicNonLifeLicenseNo}
            startDateFieldName={CPDEnrollmentFieldNames.oicNonLifeStartDate}
            endDateFieldName={CPDEnrollmentFieldNames.oicNonLifeEndDate}
            license={customerNonLifeLicense?.licenseNo}
            startDate={
              customerNonLifeLicense?.createdDate ? dateFormatThai(customerNonLifeLicense?.createdDate!) : undefined
            }
            endDate={
              customerNonLifeLicense?.expiredDate ? dateFormatThai(customerNonLifeLicense?.expiredDate!) : undefined
            }
            disabled={!isRegulatorUnchecked}
          />
        </Box>
      )}
    </Box>
  );
};
export default OICLicenseFourthPlusRenewalForm;
